
import { Device } from '@twilio/voice-sdk';
import { getAccessToken } from './tokenService';
import { logTwilioError } from '../debug';
import { logger } from '@/utils/logger';

let device: Device | null = null;

/**
 * Refresh token and reinitialize device when token expires
 */
const refreshTokenAndReinitialize = async () => {
  try {
    logger.debug('🔄 Refreshing token and reinitializing device...');
    const newToken = await getAccessToken();
    
    if (device) {
      device.updateToken(newToken);
      logger.debug('✅ Token refreshed and device updated');
    } else {
      logger.debug('🔄 Device not found, reinitializing...');
      await initializeDevice();
    }
  } catch (error) {
    logger.error('❌ Failed to refresh token and reinitialize', error);
    logTwilioError(error, 'refreshTokenAndReinitialize');
  }
};

/**
 * Initialize Twilio Voice Device
 */
export const initializeDevice = async () => {
  try {
    logger.debug('🎙️ Getting Twilio access token...');
    const token = await getAccessToken();
    
    logger.debug('🎙️ Creating Voice Device...');
    // Disable all TwilioVoice logs since user wants reduced logging
    device = new Device(token, {
      logLevel: 0,
    });

    // Add token refresh handler for when tokens expire
    device.on('tokenWillExpire', async () => {
      logger.warn('🔄 Token will expire soon, refreshing...');
      try {
        const newToken = await getAccessToken();
        device?.updateToken(newToken);
        logger.debug('✅ Token refreshed successfully');
      } catch (error) {
        logger.error('❌ Failed to refresh token', error);
        logTwilioError(error, 'tokenRefresh');
      }
    });

    device.on('error', (error) => {
      logger.error('🚨 Twilio Device Error', error);
      if (error.code === 20104) { // AccessTokenExpired
        logger.warn('🔄 Access token expired, attempting to refresh...');
        refreshTokenAndReinitialize();
      }
    });

    logger.debug('🎙️ Registering Voice Device...');
    await device.register();
    
    logger.debug('✅ Voice Device initialized successfully');
    return {
      success: true,
      device
    };
  } catch (error: any) {
    logger.error('❌ Failed to initialize Voice Device', error);
    logTwilioError(error, 'initializeDevice');
    
    // Provide specific error messages based on the error type
    let errorMessage = error.message || 'Failed to initialize Voice Device';
    
    // Handle credential-specific errors
    if (error.message?.includes('Invalid credentials') || 
        error.message?.includes('INVALID_CRE') ||
        error.message?.includes('authentication failed')) {
      errorMessage = 'Voice calling failed due to invalid Twilio credentials. Please verify your production environment variables (VITE_TWILIO_SID and VITE_TWILIO_AUTH) are correctly set and valid.';
    } else if (error.message?.includes('token endpoint not available')) {
      errorMessage = 'Voice calling requires backend configuration. Please contact your administrator to set up the Twilio Voice SDK token endpoint.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Get the current device instance
 */
export const getDevice = () => {
  return device;
};

/**
 * Get device status
 */
export const getDeviceStatus = () => {
  if (!device) return 'unregistered';
  return device.state || 'unknown';
};

/**
 * Destroy the device
 */
export const destroyDevice = () => {
  if (device) {
    device.destroy();
    device = null;
    logger.debug('🗑️ Voice Device destroyed');
  }
};
